import * as React from "react";
import RootStore from "#stores/index";
import { RouteItemProps, RouteItemPropsV2 } from "./navigation-with-permissions";

/**
 * Extrai todas as URLs de uma rota, incluindo URLs alternativas e submenus
 * @param route - Rota a ser extraída
 * @returns Array de URLs com as informações de restrição de vendor
 */
type RouteLike = {
	url?: string;
	alternativeUrls?: string[];
	allowWhenVendorRestricted?: boolean;
};

function extractUrlsFromRoute(route: RouteItemProps | RouteItemPropsV2) {
	// 1. Helper para extrair URLs de um único item (seja rota ou submenu)
	const getUrls = (item: RouteLike) => {
		const urls = item.url
			? [{ url: item.url, allowWhenVendorRestricted: item.allowWhenVendorRestricted }]
			: [];

		const alternatives = (item.alternativeUrls || []).map(url => ({
			url,
			allowWhenVendorRestricted: item.allowWhenVendorRestricted,
		}));

		return [...urls, ...alternatives];
	};

	// 2. Extrai da rota principal
	const mainUrls = getUrls(route);

	// 3. Extrai dos submenus (se existirem) usando flatMap para achatar o array
	const submenus = "submenus" in route ? route.submenus || [] : [];
	const submenuUrls = submenus.flatMap(getUrls);

	return [...mainUrls, ...submenuUrls];
}

/**
 * Normaliza uma URL removendo query params e hash para comparação
 * @param url - URL a ser normalizada
 * @returns URL normalizada
 */
function normalizeUrl(url: string): string {
	try {
		const urlObj = new URL(url, window.location.origin);
		return urlObj.pathname;
	} catch {
		return url.split("?")[0].split("#")[0];
	}
}

/**
 * Verifica se uma URL corresponde a uma rota bloqueada
 * @param currentPath - Caminho atual
 * @param routes - Array de rotas a verificar
 * @param hasVendorRestrictions - Indica se há restrições de vendor
 * @returns true se a URL corresponde a uma rota bloqueada, false caso contrário
 */
function isUrlBlocked(
	currentPath: string,
	routes: Array<RouteItemProps | RouteItemPropsV2>,
	hasVendorRestrictions: boolean,
): boolean {
	if (!hasVendorRestrictions) return false;

	const normalizedCurrent = normalizeUrl(currentPath);

	// Extraí todas as URLs de todas as rotas uma única vez
	const allUrls = routes.flatMap(extractUrlsFromRoute);

	const isMatch = (url: string) => {
		const normalized = normalizeUrl(url);
		return (
			normalizedCurrent === normalized ||
			(normalized !== "/" && normalizedCurrent.startsWith(`${normalized}/`))
		);
	};

	// 1. Se der match com alguma URL que é PERMITIDA, não está bloqueado.
	const isAllowed = allUrls.some(u => u.allowWhenVendorRestricted && isMatch(u.url));
	if (isAllowed) return false;

	// 2. Se der match com qualquer URL do sistema (e não passou na regra acima), está bloqueado.
	return allUrls.some(u => isMatch(u.url));
}

/**
 * Hook que protege rotas contra acesso direto quando há restrições de vendor
 *
 * @param routes - Array de rotas a verificar
 * @param context - Contexto de navegação com informações sobre restrições
 */
export function useVendorRestrictionGuard(
	routes: Array<RouteItemProps | RouteItemPropsV2>,
	context?: { hasVendorRestrictions?: boolean },
) {
	const { routerStore } = React.useContext(RootStore);

	const hasVendorRestrictions = context?.hasVendorRestrictions ?? false;

	React.useEffect(() => {
		const currentPath = routerStore?.location?.pathname || window.location.pathname;
		if (isUrlBlocked(currentPath, routes, hasVendorRestrictions)) {
			routerStore?.replace("/");
		}
	}, [routerStore?.location?.pathname, routes, hasVendorRestrictions, routerStore]);
}

/**
 * Hook que redireciona para a primeira rota permitida quando há restrições de vendor
 *
 * @param hasVendorRestrictions - Indica se há restrições de vendor
 * @param currentRoute - Rota atual
 * @param allowedRoutesWhenVendorRestricted - Array de rotas permitidas quando há restrições de vendor
 */
export function useVendorRestrictionRedirect(
	hasVendorRestrictions: boolean,
	currentRoute: string,
	allowedRoutesWhenVendorRestricted: string[],
) {
	React.useEffect(() => {
		if (!hasVendorRestrictions) return;
		if (!currentRoute) return;

		const normalizeRoute = (route: string) =>
			route.startsWith("/") ? route.slice(1) : route;
		const normalizedCurrentRoute = normalizeRoute(currentRoute);

		const normalizedAllowedRoutes = allowedRoutesWhenVendorRestricted.map(normalizeRoute);

		const isAllowedRoute = normalizedAllowedRoutes.includes(normalizedCurrentRoute);

		if (!isAllowedRoute) {
			const firstAllowedRoute = allowedRoutesWhenVendorRestricted[0];

			if (firstAllowedRoute) {
				const hash = firstAllowedRoute.startsWith("/")
					? firstAllowedRoute
					: `/${firstAllowedRoute}`;
				window.location.hash = hash;
			}
		}
	}, [hasVendorRestrictions, currentRoute, allowedRoutesWhenVendorRestricted]);
}
